<script setup lang="ts">
import { type MessageSchema } from '@/i18n';
import { usePostitsStore } from '@/stores/postitsStore';
import { useUserStore } from '@/stores/useUserStore';
import type { Postit } from '@/types/planning';
import DayPostitView from '@/views/Employee/DayPostitView.vue';
import Button from 'primevue/button';
import ScrollTop from 'primevue/scrolltop';
import { computed, nextTick, onActivated, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const isLoadingWeekDays = ref(false);
const postitsStore = usePostitsStore();
const scrollContainer = ref<HTMLElement | null>(null);
const user = ref(useUserStore().user);
const restaurantId = user.value?.restaurants[0]?.id || null;
const router = useRouter();

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

const loadedDays = computed(() => {
  if (!postitsStore.loadedWindow?.dates) return [];
  return postitsStore.loadedWindow.dates.map((dateStr) => new Date(dateStr)).sort((a, b) => a.getTime() - b.getTime());
});

async function loadPrev() {
  // Save current scroll height
  const prevHeight = scrollContainer.value?.scrollHeight || 0;
  isLoadingWeekDays.value = true;
  await postitsStore.loadPreviousDays();
  isLoadingWeekDays.value = false;
  // Wait for DOM update, then adjust scroll
  await nextTick();
  if (scrollContainer.value) {
    const newHeight = scrollContainer.value.scrollHeight;
    scrollContainer.value.scrollTop += newHeight - prevHeight;
  }
}

async function loadNext() {
  isLoadingWeekDays.value = true;
  await postitsStore.loadNextDays();
  isLoadingWeekDays.value = false;
  // No scroll adjustment needed for loading next (unless you want to scroll to bottom)
}

function addPostit() {
  const newPostit: Postit = {
    id: -1, // Placeholder ID, will be replaced by actual ID on server response
    tempId: Date.now(), // Temporary ID for local use
    // title: '',
    is_done: false,
    planned_date: new Date().toISOString().split('T')[0],
    restaurant_id: 'default_restaurant_id',
  };

  postitsStore.addPostit(newPostit, restaurantId, router);
}

onMounted(async () => {
  postitsStore.shouldRefetch = true;
  const restaurantId = user.value?.restaurants?.[0]?.id;
  if (restaurantId) {
    isLoadingWeekDays.value = true;
    await postitsStore.fetchInitialWindow(restaurantId);
    isLoadingWeekDays.value = false;
  }

  // Listen for visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

async function handleVisibilityChange() {
  console.log('VISIBILITY CHANGE', document.visibilityState);
  if (document.visibilityState === 'visible') {
    const restaurantId = user.value?.restaurants?.[0]?.id;
    if (restaurantId) {
      postitsStore.shouldRefetch = true;
      isLoadingWeekDays.value = true;
      await postitsStore.fetchInitialWindow(restaurantId);
      isLoadingWeekDays.value = false;
    }
  }
}

onActivated(async () => {
  const restaurantId = user.value?.restaurants?.[0]?.id;
  if (restaurantId) {
    postitsStore.shouldRefetch = true;
    isLoadingWeekDays.value = true;
    await postitsStore.fetchInitialWindow(restaurantId);
    isLoadingWeekDays.value = false;
  }
});
</script>

<template>
  <ScrollTop
    :pt="{
      root: '!fixed !top-12 !right-6',
    }"
  />
  <main class="relative">
    <div class="absolute right-2 flex items-end justify-end p-2">
      <Button v-if="postitsStore.loadedWindow?.has_prev" icon="pi pi-history" text :disabled="isLoadingWeekDays" class="w-11" @click="loadPrev" />
    </div>
    <!-- <div v-if="isLoadingWeekDays" class="flex items-center justify-center w-full h-full">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
    </div> -->
    <div v-if="loadedDays.length > 0" ref="scrollContainer" class="flex flex-col gap-2 overflow-hidden">
      <div v-for="day in loadedDays" :key="day.toISOString()">
        <DayPostitView :date="day" />
      </div>
      <div class="flex w-full items-center justify-center gap-2 p-2">
        <Button
          v-if="postitsStore.loadedWindow?.has_next"
          icon="pi pi-angle-down"
          :label="t('employee.postits.nextDays')"
          text
          size="small"
          :disabled="isLoadingWeekDays"
          @click="loadNext"
        />
      </div>
    </div>
    <div v-else>
      <div class="flex w-full flex-col items-center justify-normal gap-4 text-gray-500">
        <span class="text-center">{{ t('employee.postits.noTaskListYet') }}</span>
        <!-- <Button icon="pi pi-plus" :label="t('employee.postits.addFirst')" @click="addPostit"
          :disabled="isLoadingWeekDays" /> -->
        <div class="relative flex cursor-pointer" @click="addPostit">
          <div class="absolute inset-0 z-10 flex items-center justify-center">
            <button class="flex aspect-square w-10 items-center justify-center rounded-full bg-accent p-1 text-white">
              <i class="pi pi-plus" style="font-size: 1rem"></i>
            </button>
          </div>
          <div class="relative mx-2 my-4 flex h-56 w-44 flex-grow items-start rounded-xl bg-primary-200 px-4 py-8 opacity-25 shadow-lg">
            <div
              class="absolute -top-4 left-1/2 max-w-36 -translate-x-1/2 overflow-hidden truncate text-ellipsis whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-center font-bold text-xs text-black"
            >
              <p class="h-4 w-24"></p>
            </div>
            <div class="flex flex-col justify-around gap-3 text-sm"></div>
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="!isLoadingWeekDays"
      class="!fixed bottom-24 right-8 flex h-11 w-11 items-center justify-center rounded-full bg-accent hover:bg-accent-light"
      rounded
:style="{ bottom: 'calc(6rem + var(--safe-area-inset-bottom, 0px))' }"
      :disabled="isLoadingWeekDays"
      @click="addPostit"
    >
      <i class="pi pi-plus w-4 text-white"></i>
    </button>
  </main>
</template>
